import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ScanLine, Gift, Building2, LogOut, ShieldCheck, Sparkles, Store } from "lucide-react";
import { adminApi } from "../../lib/api";
import { FundleLogo } from "../../components/Brand";

export default function AdminShell() {
    const navigate = useNavigate();
    const [ok, setOk] = useState(false);
    useEffect(() => {
        const t = localStorage.getItem("fx_admin_token");
        if (!t) {
            navigate("/admin/login", { replace: true });
            return;
        }
        adminApi.get("/api/admin/stats").then(() => setOk(true)).catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    if (!ok) return <div className="p-10 text-[#6b7280]">Loading admin…</div>;

    const nav = [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
        { to: "/admin/bills", label: "Bills & Fraud", icon: ScanLine },
        { to: "/admin/customers", label: "Customers", icon: Users },
        { to: "/admin/brands", label: "Brands", icon: Store },
        { to: "/admin/rewards", label: "Rewards", icon: Gift },
        { to: "/admin/loyalty-rules", label: "Loyalty Rules", icon: Sparkles },
        { to: "/admin/redemptions", label: "Redemptions", icon: Building2 },
    ];

    return (
        <div className="min-h-screen flex bg-white text-[#0b1437]">
            <aside className="w-64 border-r border-[rgba(11,20,55,0.10)] px-5 py-6 hidden lg:flex flex-col">
                <Link to="/" className="mb-2"><FundleLogo /></Link>
                <span className="fx-chip bg-[#f9fafb] text-[#6b7280] border border-[rgba(11,20,55,0.10)] self-start mt-1">
                    <ShieldCheck size={11} /> Admin Console
                </span>
                <nav className="flex-1 mt-8 space-y-1">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all ${
                                    isActive
                                        ? "bg-[#c9a959]/15 text-[#c9a959] border border-[#c9a959]/30"
                                        : "text-[#4b5563] hover:bg-[#f9fafb]"
                                }`
                            }
                        >
                            <n.icon size={16} /> {n.label}
                        </NavLink>
                    ))}
                </nav>
                <button
                    onClick={() => {
                        localStorage.removeItem("fx_admin_token");
                        navigate("/admin/login");
                    }}
                    data-testid="admin-logout-btn"
                    className="fx-btn-ghost !py-2 text-sm w-full"
                >
                    <LogOut size={14} /> Sign out
                </button>
            </aside>

            <main className="flex-1 overflow-y-auto">
                {/* mobile header */}
                <div className="lg:hidden fx-glass border-b border-[rgba(11,20,55,0.10)] px-5 py-3 flex items-center gap-3">
                    <FundleLogo className="h-7" />
                    <span className="text-xs text-[#6b7280]">Admin</span>
                    <button
                        onClick={() => {
                            localStorage.removeItem("fx_admin_token");
                            navigate("/admin/login");
                        }}
                        className="ml-auto text-xs text-[#6b7280]"
                    >
                        <LogOut size={14} />
                    </button>
                </div>
                <div className="lg:hidden flex gap-1 overflow-x-auto no-scrollbar px-4 py-3 border-b border-[rgba(11,20,55,0.10)]">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            className={({ isActive }) =>
                                `shrink-0 px-3 py-1.5 rounded-full text-xs transition-all ${
                                    isActive ? "bg-[#c9a959] text-black" : "bg-[#f9fafb] text-[#4b5563]"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </div>
                <div className="p-6">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
